/**
 * DrmQuotaService - Manages DRM quota for offline downloads
 *
 * Handles:
 * - Checking if book has DRM enabled
 * - Fetching download quota from API
 * - Calculating remaining quota based on sliding window
 * - Requesting download permission (consumes quota)
 * - Caching quota info for offline use
 *
 * Quota Patterns:
 * - 10 chapters / 7 days = sliding window (normal)
 * - 999999 chapters / 1 day = unlimited
 * - 1 chapter / 999999 days = one-time only
 */

import { getDownloadQuotaRemaining, requestDownload } from '@/api/drmQuotasService';
import type { DrmQuotaRemainingDto, DrmRequestResultDto } from '@/api/drmQuotasService';
import type { BookMetadataDto } from '@/types/book';
import type { DrmQuotaCache, DrmQuotaCheckResult } from '@/types/offline';

export class DrmQuotaService {
  private quotaCache: Map<string, DrmQuotaCache> = new Map();

  /**
   * Check if book has DRM enabled
   */
  isDrmEnabled(bookMetadata: BookMetadataDto): boolean {
    return bookMetadata.isDrmEnabled;
  }

  /**
   * Check if quota allows downloads (special patterns)
   */
  isUnlimitedQuota(quotaLimit: number, quotaPeriodDays: number): boolean {
    // Pattern: 999999+ chapters / 1 day = unlimited
    return quotaLimit >= 999999 && quotaPeriodDays === 1;
  }

  /**
   * Check if quota is one-time only
   */
  isOneTimeOnlyQuota(quotaLimit: number, quotaPeriodDays: number): boolean {
    // Pattern: 1-5 chapters / 999999+ days = one-time only
    return quotaLimit <= 5 && quotaPeriodDays >= 999999;
  }

  /**
   * Get download quota for a book
   */
  async getQuota(userId: string, bookId: string): Promise<DrmQuotaCheckResult> {
    try {
      const quotaInfo = await getDownloadQuotaRemaining(userId, bookId);

      const isUnlimited = this.isUnlimitedQuota(
        quotaInfo.remaining + 1, // Estimate limit
        7 // Default window, we don't have this from API
      );

      return {
        canDownload: quotaInfo.remaining > 0 || isUnlimited,
        remainingQuota: quotaInfo.remaining,
        quotaLimit: quotaInfo.remaining + 1, // Rough estimate
        quotaPeriodDays: 7, // Default, backend should provide this
        reason: quotaInfo.remaining === 0 ? 'Download quota exhausted' : undefined,
      };
    } catch (error) {
      console.error('Failed to fetch download quota:', error);
      throw new Error('Failed to fetch download quota');
    }
  }

  /**
   * Request permission to download a chapter/page
   * This consumes quota
   */
  async requestChapterDownload(
    userId: string,
    bookId: string,
    chapterIndex: number
  ): Promise<{ approved: boolean; message: string; remainingQuota: number }> {
    try {
      const result = await requestDownload({
        studentId: userId,
        bookId,
        chapterId: `chapter-${chapterIndex}`, // Use chapter index as ID
      });

      return {
        approved: result.isApproved,
        message: result.message,
        remainingQuota: result.remainingQuota,
      };
    } catch (error) {
      console.error('Failed to request chapter download:', error);
      return {
        approved: false,
        message: error instanceof Error ? error.message : 'Failed to request download',
        remainingQuota: 0,
      };
    }
  }

  /**
   * Request permission to download multiple chapters
   * Returns list of approved chapter indices
   */
  async requestMultipleChapters(
    userId: string,
    bookId: string,
    chapterIndices: number[]
  ): Promise<{
    approvedChapters: number[];
    rejectedChapters: number[];
    remainingQuota: number;
    errors: Array<{ chapterIndex: number; message: string }>;
  }> {
    const approvedChapters: number[] = [];
    const rejectedChapters: number[] = [];
    const errors: Array<{ chapterIndex: number; message: string }> = [];
    let remainingQuota = 0;

    for (const chapterIndex of chapterIndices) {
      const result = await this.requestChapterDownload(userId, bookId, chapterIndex);

      if (result.approved) {
        approvedChapters.push(chapterIndex);
        remainingQuota = result.remainingQuota;
      } else {
        rejectedChapters.push(chapterIndex);
        errors.push({
          chapterIndex,
          message: result.message,
        });
      }

      // Stop if quota exhausted
      if (result.remainingQuota === 0) {
        // Add remaining chapters to rejected
        const remaining = chapterIndices.slice(chapterIndices.indexOf(chapterIndex) + 1);
        rejectedChapters.push(...remaining);
        break;
      }
    }

    return {
      approvedChapters,
      rejectedChapters,
      remainingQuota,
      errors,
    };
  }

  /**
   * Check if book can be downloaded (no DRM or has quota)
   */
  async canDownloadBook(
    userId: string,
    bookMetadata: BookMetadataDto
  ): Promise<{ canDownload: boolean; reason?: string }> {
    // No DRM = can download freely
    if (!bookMetadata.isDrmEnabled) {
      return { canDownload: true };
    }

    // Has DRM = check quota
    try {
      const quotaResult = await this.getQuota(userId, bookMetadata.id);

      if (quotaResult.remainingQuota === 0) {
        return {
          canDownload: false,
          reason: `Download quota exhausted. Resets in ${quotaResult.quotaPeriodDays} days.`,
        };
      }

      return { canDownload: true };
    } catch (error) {
      return {
        canDownload: false,
        reason: 'Failed to check download quota',
      };
    }
  }

  /**
   * Get downloadable chapter count based on quota
   */
  async getDownloadableChapterCount(
    userId: string,
    bookId: string
  ): Promise<number> {
    try {
      const quotaResult = await this.getQuota(userId, bookId);
      return quotaResult.remainingQuota;
    } catch (error) {
      console.error('Failed to get downloadable chapter count:', error);
      return 0;
    }
  }

  /**
   * Cache quota info for offline use
   */
  cacheQuota(userId: string, bookId: string, quotaInfo: DrmQuotaCache): void {
    const cacheKey = `${userId}-${bookId}`;
    this.quotaCache.set(cacheKey, quotaInfo);
  }

  /**
   * Get cached quota info
   */
  getCachedQuota(userId: string, bookId: string): DrmQuotaCache | undefined {
    const cacheKey = `${userId}-${bookId}`;
    return this.quotaCache.get(cacheKey);
  }

  /**
   * Clear quota cache
   */
  clearCache(): void {
    this.quotaCache.clear();
  }
}

// Singleton instance
export const drmQuotaService = new DrmQuotaService();
